import React from 'react';
import { motion } from 'framer-motion';
import { Clock, TrendingUp, Calculator, FileText, Eye } from 'lucide-react';
import { useAuth } from '@/contexts/SupabaseAuthContext';

interface ActivityItem {
  id: string;
  title: string;
  description: string;
  time: string;
  icon: React.ComponentType<any>;
  type: 'calculation' | 'analysis' | 'report' | 'view';
}

export const RecentActivity: React.FC = () => {
  const { user } = useAuth();

  // Mock recent activity data - in real app, this would come from user data
  const recentActivities: ActivityItem[] = [
    {
      id: '1',
      title: 'Buying Power Calculated',
      description: 'Updated financial assessment with $485K buying power',
      time: '2 hours ago',
      icon: Calculator,
      type: 'calculation'
    },
    {
      id: '2',
      title: 'Market Analysis Viewed',
      description: 'Analyzed 12 properties in downtown area',
      time: '1 day ago',
      icon: TrendingUp,
      type: 'analysis'
    },
    {
      id: '3',
      title: 'Financial Report Generated',
      description: 'Created detailed affordability report',
      time: '2 days ago',
      icon: FileText,
      type: 'report'
    },
    {
      id: '4',
      title: 'Property Comparison',
      description: 'Compared 3 properties in your price range',
      time: '3 days ago',
      icon: Eye,
      type: 'view'
    }
  ];

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'calculation': return 'text-blue-400';
      case 'analysis': return 'text-green-400';
      case 'report': return 'text-purple-400';
      case 'view': return 'text-cyan-400';
      default: return 'text-muted-foreground';
    }
  };

  const getActivityBg = (type: string) => {
    switch (type) {
      case 'calculation': return 'bg-blue-500/20';
      case 'analysis': return 'bg-green-500/20';
      case 'report': return 'bg-purple-500/20';
      case 'view': return 'bg-cyan-500/20';
      default: return 'bg-muted/20';
    }
  };

  if (!user) return null;

  return (
    <div className="p-6 bg-background/50 backdrop-blur-sm border border-border/50 rounded-lg">
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center gap-3 mb-6"
      >
        <Clock className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Recent Activity</h2>
      </motion.div>

      <div className="space-y-4">
        {recentActivities.map((activity, index) => (
          <motion.div
            key={activity.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors duration-200"
          >
            <div className={`p-2 rounded-lg ${getActivityBg(activity.type)}`}>
              <activity.icon className={`w-4 h-4 ${getActivityColor(activity.type)}`} />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-foreground text-sm">{activity.title}</h3>
              <p className="text-xs text-muted-foreground mt-1 truncate">
                {activity.description}
              </p>
              <span className="text-xs text-muted-foreground">{activity.time}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {recentActivities.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-8"
        >
          <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No recent activity</p>
          <p className="text-sm text-muted-foreground">Complete your first step to get started</p>
        </motion.div>
      )}
    </div>
  );
};